/**
 * Amplifier profiles -- group 04 (Japanese integrated, power and tube designs).
 *
 * The engineering interest in this group is that three superficially similar
 * Japanese flagships take three completely different routes to the loudspeaker:
 * a heavily biased Class A output with error-correction feedback, a conventional
 * high-feedback Class AB bipolar output, and two quite different switching
 * implementations -- one an analog-input Ncore module, the other a full-digital
 * amplifier where PWM is generated in the digital domain and the output filter
 * is load-corrected. A 300B single-ended triode closes the group as the
 * extreme-low-feedback reference point.
 *
 * Published figures are tagged MANUFACTURER_SPEC. Where a Japanese manufacturer
 * publishes power and bandwidth but not damping factor or crosstalk -- which is
 * common -- those terms are ESTIMATED and the provenanceNote says so.
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeAmp, usd } from '../kit';

export const AMPS_04: AmpProfile[] = [
  makeAmp({
    mfr: 'Luxman',
    model: 'L-590AX Mark II',
    price: usd(9495, 'APPROXIMATE', 2022, 'Approximate US retail; recalled figure rather than a current dealer quotation.'),
    year: 2018,
    country: 'Japan',
    topology: 'solid-state-a',
    form: 'integrated',
    devices: 'Parallel push-pull bipolar output biased into Class A throughout the rated range, ODNF version 4.0 error-correction feedback',
    power: { watts8: 30, watts4: 60, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 36,
    thdPercent: 0.003,
    snrDb: 107,
    bandwidth: [10, 100000],
    dampingFactor: 210,
    feedbackDb: 10,
    crosstalkDb: -92,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.42, asymmetry: 0.45, bias: 0.6, headroomDb: 13, oversample: 4 },
    dynamics: { sagDb: 0.3, sagTimeMs: 50, recoveryMs: 130, thermalDb: 0.45, thermalTimeMs: 9000, slewRateVus: 55, headroomDb: 1.4 },
    clipping: { kind: 'asymmetric', kneeDb: 4.0, stickiness: 0.14 },
    confidence: 'ESTIMATED',
    accent: '#d9b26a',
    tags: ['class-a', 'odnf', 'high-bias', 'japanese-flagship'],
    notes:
      'The output devices never leave conduction within the rated 30 W, so there is no crossover region at all and the residual distortion is a smoothly rising, second-harmonic-dominated term rather than the notch-shaped residue of a Class AB stage. ODNF applies feedback only around the detected error signal rather than as a large global loop, which keeps the loop gain -- and therefore the higher-order product generation near clipping -- modest by solid-state standards. Thermal drift is given a larger and slower term than any other entry here because a continuously high-bias output stage genuinely moves its operating point as the heatsinks come up to temperature.',
    provenanceNote:
      'Rated power into 8 and 4 ohms, the Class A bias scheme and the ODNF feedback topology are Luxman published material. THD, signal-to-noise ratio, bandwidth and damping factor are ESTIMATED from Luxman specifications for this series; the specific feedback figure in dB is inferred, since Luxman describes ODNF qualitatively rather than quoting loop gain. Thermal and sag behaviour are modelled.',
  }),

  makeAmp({
    mfr: 'Yamaha',
    model: 'A-S3200',
    price: usd(7999, 'APPROXIMATE', 2022, 'Approximate US retail; recalled figure rather than a current dealer quotation.'),
    year: 2020,
    country: 'Japan',
    topology: 'solid-state-ab',
    form: 'integrated',
    devices: 'Floating and balanced fully symmetrical power amplifier stage, paralleled bipolar output, dual-mono toroidal supply',
    power: { watts8: 100, watts4: 150, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 36,
    thdPercent: 0.07,
    snrDb: 105,
    bandwidth: [5, 100000],
    dampingFactor: 250,
    feedbackDb: 30,
    crosstalkDb: -100,
    harmonicShape: HARMONIC_SHAPES.classAB,
    nonlinear: { kind: 'class-b-crossover', amount: 0.4, crossoverWidth: 0.0035, headroomDb: 11, asymmetry: 0.1, oversample: 4 },
    dynamics: { sagDb: 0.4, sagTimeMs: 55, recoveryMs: 150, thermalDb: 0.1, thermalTimeMs: 6000, slewRateVus: 70, headroomDb: 2.4 },
    clipping: { kind: 'asymmetric', kneeDb: 2.2, stickiness: 0.07 },
    confidence: 'ESTIMATED',
    accent: '#7f9ec9',
    tags: ['class-ab', 'floating-balanced', 'high-current', 'japanese-flagship'],
    notes:
      'The floating and balanced arrangement drives both ends of the load from symmetrical halves of the circuit, which cancels even-order products in the output stage and leaves a residue that is odd-order and crossover-shaped. That is the direct opposite of the Luxman entry in this group: same country, similar price, but the modelled distortion sits at a fixed low level and is dominated by the third and fifth harmonics rather than the second. The stated 0.07 percent figure is a wide-band 20 Hz to 20 kHz number, so this profile treats the distortion as roughly frequency-independent rather than collapsing at 1 kHz.',
    provenanceNote:
      'Rated power into 8 and 4 ohms, the wide-band THD figure, signal-to-noise ratio, damping factor and the floating and balanced topology are Yamaha published specifications. The feedback figure in dB, crosstalk, crossover-notch width and all dynamic and clipping terms are ESTIMATED from Class AB norms at this power and feedback level.',
  }),

  makeAmp({
    mfr: 'Denon',
    model: 'PMA-A110',
    price: usd(4499, 'APPROXIMATE', 2021, 'Approximate US retail for this 110th-anniversary limited model; recalled figure.'),
    year: 2020,
    country: 'Japan',
    topology: 'solid-state-ab',
    form: 'integrated',
    devices: 'UHC-MOS single push-pull output, one device pair per channel, Advanced High Current supply',
    power: { watts8: 80, watts4: 160, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 35,
    thdPercent: 0.01,
    snrDb: 107,
    bandwidth: [5, 100000],
    dampingFactor: 120,
    feedbackDb: 24,
    crosstalkDb: -96,
    harmonicShape: HARMONIC_SHAPES.classAB,
    nonlinear: { kind: 'class-b-crossover', amount: 0.48, crossoverWidth: 0.0055, headroomDb: 10, asymmetry: 0.28, oversample: 4 },
    dynamics: { sagDb: 0.55, sagTimeMs: 70, recoveryMs: 180, thermalDb: 0.14, thermalTimeMs: 6500, slewRateVus: 60, headroomDb: 3.0 },
    clipping: { kind: 'asymmetric', kneeDb: 2.8, stickiness: 0.1 },
    confidence: 'ESTIMATED',
    accent: '#c07a6a',
    tags: ['uhc-mos', 'single-push-pull', 'class-ab', 'anniversary-model'],
    notes:
      'Using one very large MOSFET pair per channel instead of several paralleled pairs removes the device-matching and current-sharing errors that paralleled outputs introduce, but it also means the single pair must traverse its own crossover region unaided, so the notch is wider and more asymmetric than in a paralleled bipolar design. This profile therefore gives it a larger crossover width and a higher asymmetry term than the Yamaha entry while keeping the total THD figure lower. The doubling of rated power from 8 to 4 ohms indicates a stiff supply, which is reflected in a modest sag term and generous short-term headroom.',
    provenanceNote:
      'Rated power into 8 and 4 ohms, the UHC-MOS single push-pull output arrangement and the signal-to-noise ratio are Denon published material. THD, damping factor, feedback in dB, crosstalk and all nonlinear and dynamic terms are ESTIMATED; Denon quotes distortion only at a single frequency and load in its published sheet, so the value here should be read as a class-typical figure rather than a transcription.',
  }),

  makeAmp({
    mfr: 'Marantz',
    model: 'PM-10',
    price: usd(7999, 'APPROXIMATE', 2019, 'Approximate US retail while current; recalled figure.'),
    year: 2017,
    country: 'Japan',
    topology: 'class-d',
    form: 'integrated',
    devices: 'Hypex Ncore NC500 output module per channel, driven by a Marantz HDAM-SA2 discrete current-feedback preamplifier stage',
    power: { watts8: 200, watts4: 400, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 37,
    thdPercent: 0.005,
    snrDb: 112,
    bandwidth: [5, 50000],
    dampingFactor: 500,
    feedbackDb: 30,
    crosstalkDb: -105,
    harmonicShape: HARMONIC_SHAPES.classD,
    nonlinear: { kind: 'class-d-pwm', amount: 0.3, carrierHz: 500000, headroomDb: 8, asymmetry: 0.08, oversample: 4 },
    dynamics: { sagDb: 0.12, sagTimeMs: 30, recoveryMs: 70, thermalDb: 0.05, thermalTimeMs: 5000, slewRateVus: 28, headroomDb: 0.9 },
    clipping: { kind: 'hard', kneeDb: 0.5, stickiness: 0.06 },
    confidence: 'ESTIMATED',
    accent: '#6a8fd0',
    tags: ['class-d', 'ncore', 'discrete-preamp', 'hybrid-signal-path'],
    notes:
      'The signal path is deliberately split: a discrete current-feedback voltage stage sets the tonal signature, then a self-oscillating switching module with very high loop gain does the current delivery. Because the module closes its feedback loop after the output filter, the frequency response is largely independent of loudspeaker impedance -- unlike a switching amplifier that takes feedback before the filter, where the filter Q and therefore the treble balance move with the load. The distortion this profile applies is low, odd-order and essentially level-independent, with a hard clipping characteristic and almost no supply sag.',
    provenanceNote:
      'Rated power into 8 and 4 ohms, the use of Hypex Ncore output modules and the discrete current-feedback preamplifier are from Marantz published material. THD, signal-to-noise ratio, damping factor, crosstalk and the switching frequency are ESTIMATED from Ncore NC500 module norms rather than transcribed from a Marantz specification sheet.',
  }),

  makeAmp({
    mfr: 'Technics',
    model: 'SU-R1000',
    price: usd(9499, 'APPROXIMATE', 2022, 'Approximate US retail; recalled figure rather than a current dealer quotation.'),
    year: 2020,
    country: 'Japan',
    topology: 'class-d',
    form: 'integrated',
    devices: 'JENO Engine full-digital PWM conversion with ADCT current-feedback correction and LAPC load-adaptive phase calibration',
    power: { watts8: 150, watts4: 300, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 34,
    thdPercent: 0.008,
    snrDb: 110,
    bandwidth: [5, 60000],
    outputImpedanceOhms: 0.03,
    feedbackDb: 22,
    crosstalkDb: -100,
    harmonicShape: HARMONIC_SHAPES.classD,
    nonlinear: { kind: 'class-d-pwm', amount: 0.36, carrierHz: 768000, headroomDb: 7, asymmetry: 0.12, oversample: 4 },
    dynamics: { sagDb: 0.18, sagTimeMs: 35, recoveryMs: 80, thermalDb: 0.06, thermalTimeMs: 5000, slewRateVus: 24, headroomDb: 0.7 },
    clipping: { kind: 'hard', kneeDb: 0.35, stickiness: 0.05 },
    confidence: 'ESTIMATED',
    accent: '#8fbfc9',
    tags: ['full-digital', 'jeno-engine', 'lapc', 'class-d', 'digital-input-path'],
    notes:
      'There is no digital-to-analog converter in the conventional sense: PCM is noise-shaped straight into a pulse-width sequence and the first analog element in the chain is the output LC filter itself. That filter forms a resonant network with the loudspeaker impedance, so the amplitude and phase response genuinely change with the speaker connected, and LAPC measures that and pre-corrects it in the digital domain -- which is why this profile carries a much lower effective output impedance than its switching frequency and filter order alone would imply. Against the Marantz entry the audible differences are the higher carrier, the slightly higher and more asymmetric residual, and the fact that jitter and noise-shaper behaviour belong to the amplifier rather than to an upstream DAC.',
    provenanceNote:
      'Rated power into 8 and 4 ohms and the JENO / ADCT / LAPC processing blocks are Technics published material. THD, signal-to-noise ratio, output impedance, crosstalk, switching frequency and all dynamic terms are ESTIMATED; Technics publishes the architecture in detail but not a damping factor or a full distortion-versus-power characteristic.',
  }),

  makeAmp({
    mfr: 'Esoteric',
    model: 'Grandioso M1',
    price: usd(24000, 'APPROXIMATE', 2022, 'Approximate US retail per monoblock; a stereo system requires two. Recalled figure.'),
    year: 2015,
    country: 'Japan',
    topology: 'solid-state-ab',
    form: 'monoblock',
    devices: 'Fully balanced bridged output, four bipolar pairs per polarity, twin toroidal transformers per chassis',
    power: { watts8: 300, watts4: 600, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 29,
    thdPercent: 0.005,
    snrDb: 118,
    bandwidth: [3, 150000],
    dampingFactor: 500,
    feedbackDb: 34,
    crosstalkDb: -125,
    harmonicShape: HARMONIC_SHAPES.classAB,
    nonlinear: { kind: 'class-b-crossover', amount: 0.34, crossoverWidth: 0.0022, headroomDb: 12, asymmetry: 0.08, oversample: 4 },
    dynamics: { sagDb: 0.2, sagTimeMs: 40, recoveryMs: 110, thermalDb: 0.09, thermalTimeMs: 7000, slewRateVus: 110, headroomDb: 2.8 },
    clipping: { kind: 'asymmetric', kneeDb: 1.8, stickiness: 0.05 },
    confidence: 'ESTIMATED',
    accent: '#b0b8c4',
    tags: ['monoblock', 'bridged-balanced', 'high-power', 'high-feedback'],
    notes:
      'Bridging two balanced halves means each half sees the load referenced to the other rather than to ground, so the effective source impedance and the crossover residue of both halves add rather than cancel -- the compensating advantage is that even-order products from the two halves subtract, leaving a low, odd-dominant residue. With four output pairs per polarity the per-device current is low enough that the crossover notch is narrow, which this profile expresses as a small crossoverWidth and a high slew rate rather than as a lower total THD number. Channel separation is set very high because each channel is a separate chassis with its own supply, so there is no shared ground impedance term at all.',
    provenanceNote:
      'Rated power into 8 and 4 ohms and the bridged fully balanced monoblock topology are from Esoteric published material. THD, signal-to-noise ratio, damping factor, bandwidth, feedback in dB, device count and all dynamic terms are ESTIMATED; Esoteric publishes power and topology but not a full distortion-versus-frequency characteristic, and the power figure here should be treated as approximate.',
  }),

  makeAmp({
    mfr: 'TEAC',
    model: 'AP-701',
    price: usd(2999, 'APPROXIMATE', 2022, 'Approximate US retail; recalled figure rather than a current dealer quotation.'),
    year: 2021,
    country: 'Japan',
    topology: 'class-d',
    form: 'stereo-power',
    devices: 'Hypex Ncore output modules in a dual-mono layout with independent supplies, bridgeable to mono',
    power: { watts8: 130, watts4: 240, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 29,
    thdPercent: 0.006,
    snrDb: 108,
    bandwidth: [5, 45000],
    dampingFactor: 250,
    feedbackDb: 28,
    crosstalkDb: -108,
    harmonicShape: HARMONIC_SHAPES.classD,
    nonlinear: { kind: 'class-d-pwm', amount: 0.34, carrierHz: 430000, headroomDb: 8, asymmetry: 0.1, oversample: 4 },
    dynamics: { sagDb: 0.22, sagTimeMs: 32, recoveryMs: 85, thermalDb: 0.07, thermalTimeMs: 5000, slewRateVus: 22, headroomDb: 1.1 },
    clipping: { kind: 'hard', kneeDb: 0.45, stickiness: 0.06 },
    confidence: 'ESTIMATED',
    accent: '#7fa08f',
    tags: ['class-d', 'ncore', 'dual-mono', 'bridgeable'],
    notes:
      'Same switching family as the Marantz entry but a smaller module and a lower supply rail, and the practical difference is at the top of the range rather than in the distortion floor: bandwidth is set by the output filter corner, which sits lower here, so the response is already a few tenths of a dB down at 20 kHz before any loudspeaker is connected. The lower quoted damping factor also means loudspeaker impedance variation prints onto the response about twice as strongly as it does through the Ncore NC500 modules. Bridging to mono doubles the voltage swing but also doubles the effective source impedance, which the chain model will show as reduced damping if the user configures it that way.',
    provenanceNote:
      'The dual-mono Ncore-based construction and mono bridging capability are from TEAC published material. Rated power, THD, signal-to-noise ratio, damping factor, bandwidth and switching frequency are ESTIMATED from module norms and from recalled TEAC figures; the power ratings in particular should be treated as approximate rather than transcribed.',
  }),

  makeAmp({
    mfr: 'Air Tight',
    model: 'ATM-300R',
    price: usd(18000, 'APPROXIMATE', 2022, 'Approximate US retail; Air Tight is imported in small numbers and pricing has moved, so this is a recalled figure.'),
    year: 2017,
    country: 'Japan',
    topology: 'tube-set',
    form: 'stereo-power',
    devices: 'One 300B directly-heated triode per channel in single-ended operation, 6SN7 driver, zero global feedback, custom-wound output transformers',
    power: { watts8: 8, watts4: 6, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 27,
    thdPercent: 1.0,
    snrDb: 88,
    bandwidth: [15, 35000],
    outputImpedanceOhms: 2.4,
    feedbackDb: 0,
    outputTransformer: true,
    transformerLfHz: 20,
    transformerHfHz: 30000,
    crosstalkDb: -60,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.9, asymmetry: 0.82, bias: 0.88, headroomDb: 7, oversample: 8 },
    dynamics: { sagDb: 1.8, sagTimeMs: 230, recoveryMs: 560, thermalDb: 0.2, thermalTimeMs: 12000, slewRateVus: 15, headroomDb: 0.8 },
    clipping: { kind: 'soft', kneeDb: 8.5, stickiness: 0.38 },
    confidence: 'ESTIMATED',
    accent: '#e0a04e',
    tags: ['set', '300b', 'zero-feedback', 'directly-heated', 'output-transformer'],
    notes:
      'With no global loop the 2.4 ohm source impedance is not corrected, so the loudspeaker impedance curve is impressed directly onto the frequency response -- on a 4 ohm speaker with a 3:1 impedance swing that is more than a dB of tonal change that no specification sheet will show. Distortion is dominated by the second harmonic and rises roughly one dB per dB of drive rather than sitting at a floor, so the harmonic content is a function of listening level in a way none of the solid-state entries here are. The output transformer contributes core saturation below about 30 Hz at full output and sets the upper corner, which is why the modelled bandwidth is an order of magnitude narrower than the switching designs in this group.',
    provenanceNote:
      'The 300B single-ended zero-feedback topology, the driver tube and the 8 W rating are from Air Tight published material. THD, signal-to-noise ratio, bandwidth, output impedance and transformer corner frequencies are ESTIMATED from norms for zero-feedback 300B single-ended designs -- Air Tight, like most tube manufacturers, does not publish a distortion-versus-power characteristic. All dynamic, sag and clipping terms are modelled.',
  }),
];
